import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PiCurrencyDollarLight, PiPlusLight, PiWarningLight, PiPencilSimpleLineLight } from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import { fetchUserBillingRates, createUserBillingRate, updateUserBillingRate } from '../../../store/actions/economicsActions';
import { useOrgDefaultCurrency } from '../../../hooks/useOrgDefaultCurrency';
import { formatCurrency } from '../../../utils/formatCurrency';
import CurrencyInput from '../../shared/TailwindComponents/CurrencyInput';
import CurrencyPicker from '../../shared/TailwindComponents/CurrencyPicker';
import DateTimePicker from '../../shared/TailwindComponents/DatePicker';
import Button from '../../shared/TailwindComponents/Button';
import Table from '../../shared/TailwindComponents/Table';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import FormActions from '../../shared/FormComponents/FormActions';
import FormSection from '../../shared/FormComponents/FormSection';
import { FIELD_LABEL_CLASS } from '../../shared/TailwindComponents/FieldShell';
import clsx from 'clsx';
import { Well } from '../../shared/brand/Field';
import { ymdToDate, dateToYmd } from '../../Matters/economics/dateHelpers';
import type { UserBillingRate } from '../../../types/economics';

interface UserBillingRatePanelProps {
  userId: number | string;
  /** Firm admins may set/replace the rate; everyone else views only (US #2236). */
  canEdit?: boolean;
  /**
   * Drop the panel's own glass card and heading, leaving only the content.
   *
   * The settings "My rates" section already draws a titled card around this, and
   * a card inside a card is chrome, not grouping. Off by default so the admin
   * user-edit view, which renders the panel standalone, is unchanged.
   */
  bare?: boolean;
  /**
   * Controlled "the set/edit form is open".
   *
   * `bare` hands the section chrome to the caller, and the primary action
   * belongs in that chrome — every other personal settings section puts its
   * action in the section header, and a lone button floating over the top-right
   * of the body is what made this card read as mostly empty. Passing this pair
   * lets the header own the button while the form stays here. Omit both and the
   * panel keeps its own button and its own state, which is what the admin
   * user-edit view does.
   */
  formOpen?: boolean;
  onFormOpenChange?: (open: boolean) => void;
}

const todayStr = () => new Date().toISOString().split('T')[0];

/** The rate effective *today*: effective_from <= today and (no effective_to or effective_to >= today). */
const pickEffectiveToday = (rates: UserBillingRate[]): UserBillingRate | undefined => {
  const today = todayStr();
  return [...rates]
    .filter((r) => (r.effective_from || '') <= today && (!r.effective_to || r.effective_to >= today))
    .sort((a, b) => (b.effective_from || '').localeCompare(a.effective_from || ''))[0];
};

/**
 * Shows a user's active default billing rate + full rate history, with an inline
 * "set new rate" form for firm admins (US #2236, task #2258). Used on the user's
 * own profile (PersonalTab) and the admin user-edit view.
 */
const UserBillingRatePanel: React.FC<UserBillingRatePanelProps> = ({
  userId,
  canEdit = false,
  bare = false,
  formOpen,
  onFormOpenChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const preferredCurrency = userInfo?.preferences?.general_settings?.currency || 'EUR';
  const { defaultCurrency: orgDefaultCurrency } = useOrgDefaultCurrency();

  const emptyForm = () => ({ rate: '', currency: preferredCurrency, effective_from: todayStr(), effective_to: '' });

  const [rates, setRates] = useState<UserBillingRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [ownShowForm, setOwnShowForm] = useState(false);
  const showForm = formOpen ?? ownShowForm;
  const setShowForm = useCallback(
    (open: boolean) => {
      setOwnShowForm(open);
      onFormOpenChange?.(open);
    },
    [onFormOpenChange]
  );
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState<string>('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(false);
    try {
      const data = await dispatch(fetchUserBillingRates(userId));
      setRates(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const currentRate = useMemo(() => pickEffectiveToday(rates), [rates]);

  // Inline warnings as the user types (FIX 1).
  const futureStart = !!form.effective_from && form.effective_from > todayStr();
  const invalidRange = !!form.effective_to && !!form.effective_from && form.effective_to < form.effective_from;
  // Warn when the picked rate currency differs from the org default (#2246).
  const currencyMismatch =
    !!form.currency && !!orgDefaultCurrency &&
    form.currency.toUpperCase() !== orgDefaultCurrency.toUpperCase();

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setFormError(''); setShowForm(true); };
  const openEdit = (r: UserBillingRate) => {
    setEditingId(r.id);
    setForm({
      rate: String(r.default_rate ?? ''),
      currency: r.currency || preferredCurrency,
      effective_from: r.effective_from || todayStr(),
      effective_to: r.effective_to || '',
    });
    setFormError('');
    setShowForm(true);
  };

  // Opened from outside (the section header's button) rather than through
  // `openCreate` — set up the same fresh-rate state that button would have.
  const wasOpen = useRef(showForm);
  useEffect(() => {
    if (showForm && !wasOpen.current && editingId == null) {
      setForm({ rate: '', currency: preferredCurrency, effective_from: todayStr(), effective_to: '' });
      setFormError('');
    }
    wasOpen.current = showForm;
  }, [showForm, editingId, preferredCurrency]);

  const submit = async () => {
    const rateNum = Number(form.rate);
    if (form.rate.trim() === '' || Number.isNaN(rateNum) || rateNum <= 0) {
      setFormError(t('Rate must be greater than 0'));
      return;
    }
    if (invalidRange) {
      setFormError(t('End date must be after start date.'));
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const payload = {
        default_rate: rateNum,
        currency: form.currency,
        effective_from: form.effective_from || todayStr(),
        effective_to: form.effective_to || null,
      };
      if (editingId != null) {
        await dispatch(updateUserBillingRate(userId, editingId, payload));
      } else {
        await dispatch(createUserBillingRate(userId, payload));
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      await load();
    } catch {
      /* toast shown by the thunk */
    } finally {
      setSaving(false);
    }
  };

  const columns = [t('Rate'), t('From'), t('To')];
  const rows = [...rates]
    .sort((a, b) => (b.effective_from || '').localeCompare(a.effective_from || ''))
    .map((r) => ({
      id: r.id,
      [t('Rate')]: formatCurrency(Number(r.default_rate), r.currency),
      [t('From')]: r.effective_from || '—',
      [t('To')]: r.effective_to || t('Current'),
      _raw: r,
    }));
  const historyActions = canEdit
    ? [{ label: t('Edit'), icon: <PiPencilSimpleLineLight />, onClick: (row: any) => openEdit(row._raw as UserBillingRate) }]
    : undefined;

  /**
   * Standalone, the panel wears the same card the settings sections wear —
   * `FormSection`, i.e. a `GlassCard` with the icon/title/description header
   * and a right-aligned action — rather than a hand-rolled `glass` div with its
   * own `<h4>`. `bare` drops it because the caller already drew one.
   */
  const body = (
    <div className={bare ? 'space-y-3' : 'space-y-4'}>

      {/* The rate itself: a labelled readout in a well, so the number is a
          stated value rather than large text floating in the card. */}
      {loading ? (
        <Skeleton variant="card" />
      ) : error ? (
        <Well className="flex items-center justify-between gap-3">
          <p className="text-sm text-fg-muted">{t('Could not load billing rates.')}</p>
          <Button variant="outline" size="small" onClick={load}>{t('Retry')}</Button>
        </Well>
      ) : currentRate ? (
        <Well className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={clsx('w-full', FIELD_LABEL_CLASS)}>
            {t('Current effective rate today')}
          </span>
          <span className="text-xl font-medium text-fg-strong">
            {formatCurrency(Number(currentRate.default_rate), currentRate.currency)}
          </span>
          <span className="text-xs text-fg-muted">
            {(currentRate.currency || '').toUpperCase()} · {t('per hour')}
            {currentRate.effective_from ? ` · ${t('since')} ${currentRate.effective_from}` : ''}
          </span>
        </Well>
      ) : (
        <Well>
          <p className="text-sm text-fg-muted">{t('No active billing rate today.')}</p>
        </Well>
      )}

      {/* Inline set/edit-rate form (admins only) */}
      {canEdit && showForm && (
        <Well className="space-y-4">
          <p className={FIELD_LABEL_CLASS}>
            {editingId != null ? t('Edit billing rate') : t('Set new rate')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <CurrencyInput
              id="user-rate-amount"
              label={t('Rate')}
              value={form.rate}
              currency={form.currency}
              onChange={(rate) => setForm((p) => ({ ...p, rate }))}
              error={formError || undefined}
              required
            />
            <CurrencyPicker
              id="user-rate-currency"
              label={t('Currency')}
              value={form.currency}
              onChange={(currency) => setForm((p) => ({ ...p, currency }))}
            />
            <DateTimePicker
              id="user-rate-effective-from"
              label={t('Effective from')}
              selected={ymdToDate(form.effective_from)}
              showTimeSelect={false}
              dateFormat="yyyy-MM-dd"
              fullWidth
              onChange={(d) => setForm((p) => ({ ...p, effective_from: dateToYmd(d) }))}
            />
            <DateTimePicker
              id="user-rate-effective-to"
              // Optionality is carried by the empty-state text ("Open-ended"),
              // which says what leaving it blank means — a "(optional)"
              // parenthetical in the label only says that it may be blank.
              label={t('Effective to')}
              selected={ymdToDate(form.effective_to)}
              placeholder={t('Open-ended')}
              showTimeSelect={false}
              dateFormat="yyyy-MM-dd"
              fullWidth
              error={invalidRange ? t('End date must be after start date.') : undefined}
              onChange={(d) => setForm((p) => ({ ...p, effective_to: dateToYmd(d) }))}
            />
          </div>

          {futureStart && (
            <div className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 dark:border-warning-700/60 dark:bg-warning-900/20 p-2.5">
              <PiWarningLight className="h-4 w-4 text-warning-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning-800 dark:text-warning-300">
                {t('This rate becomes effective on {{date}}. Time logged before that won\'t have a rate.', { date: form.effective_from })}
              </p>
            </div>
          )}
          {invalidRange && (
            <div className="flex items-start gap-2 rounded-md border border-warning-300 bg-warning-50 dark:border-warning-700/60 dark:bg-warning-900/20 p-2.5">
              <PiWarningLight className="h-4 w-4 text-warning-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning-800 dark:text-warning-300">{t('End date must be after start date.')}</p>
            </div>
          )}
          {currencyMismatch && (
            <div className="flex items-start gap-2 rounded-md border border-danger-300 bg-danger-50 dark:border-danger-700/60 dark:bg-danger-900/20 p-2.5">
              <PiWarningLight className="h-4 w-4 text-danger-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-danger-800 dark:text-danger-300">
                {t("Rate currency ({{rate}}) differs from the organization default ({{org}}). Time on matters in {{org}} won't use this rate.", { rate: (form.currency || '').toUpperCase(), org: (orgDefaultCurrency || '').toUpperCase() })}
              </p>
            </div>
          )}

          <FormActions divider>
            <Button variant="outline" size="small" onClick={() => { setShowForm(false); setEditingId(null); setFormError(''); }}>
              {t('Cancel')}
            </Button>
            <Button variant="primary" size="small" onClick={submit} disabled={saving || invalidRange} isLoading={saving}>
              {t('Save')}
            </Button>
          </FormActions>
        </Well>
      )}

      {/* History — a labelled sub-block, sized to its rows rather than to a
          fixed 20rem box that stood half-empty for the usual one or two rates. */}
      {rates.length > 0 && (
        <div className="space-y-1.5">
          <p className={FIELD_LABEL_CLASS}>{t('Rate history')}</p>
          <Table
            columns={columns}
            data={rows}
            actions={historyActions}
            onRowClick={canEdit ? (row: any) => openEdit(row._raw as UserBillingRate) : undefined}
            rowSize="compact"
            min="min-h-0"
            max="max-h-[16rem]"
          />
        </div>
      )}
    </div>
  );

  if (bare) return body;

  return (
    <FormSection
      icon={<PiCurrencyDollarLight />}
      title={t('Default billing rate')}
      description={t('The default hourly rate used for time entries unless a matter sets an override.')}
      actions={
        canEdit && !showForm ? (
          <Button variant="primary" size="small" icon={<PiPlusLight />} onClick={openCreate}>
            {t('Set new rate')}
          </Button>
        ) : undefined
      }
      noGrid
    >
      {body}
    </FormSection>
  );
};

export default UserBillingRatePanel;
